/**
 * 날짜/시간 유틸리티 함수 (KST 기준)
 * 프로그램 상태 계산 시 사용
 */

/**
 * 현재 KST 시간을 ISO 문자열로 반환
 * @returns 현재 KST 시간 (예: "2025-01-15T14:30:00")
 */
export const getKSTNow = (): Date => {
  const now = new Date();
  // UTC에 9시간 더해서 KST로 변환
  return new Date(now.getTime() + 9 * 60 * 60 * 1000);
};

/**
 * 현재 KST 날짜를 YYYY-MM-DD 형식으로 반환
 */
export const getKSTDateString = (): string => {
  const kst = getKSTNow();
  return kst.toISOString().split('T')[0];
};

/**
 * 현재 KST 시간을 HH:MM 형식으로 반환
 */
export const getKSTTimeString = (): string => {
  const kst = getKSTNow();
  return kst.toISOString().split('T')[1].substring(0, 5);
};

/**
 * 날짜+시간 문자열을 KST Date 객체로 변환
 * @param dateStr 날짜 문자열 (YYYY-MM-DD)
 * @param timeStr 시간 문자열 (HH:MM 또는 HH:MM:SS), 없으면 기본값 사용
 * @param defaultTime 시간이 없을 때 기본값 ('00:00:00' 또는 '23:59:59')
 */
export const parseKSTDateTime = (
  dateStr: string,
  timeStr?: string,
  defaultTime: string = '00:00:00'
): Date => {
  const time = timeStr || defaultTime;
  // +09:00을 붙여서 KST로 파싱
  return new Date(`${dateStr}T${time.length === 5 ? time + ':00' : time}+09:00`);
};

interface CourseForStatus {
  registrationStartDate?: string;  // nullable
  registrationEndDate?: string;    // nullable
  registrationStartTime?: string;
  registrationEndTime?: string;
  courseStartDate?: string;        // nullable
  courseEndDate?: string;          // nullable
  status?: string;                 // DB 저장 상태 (manual 모드용)
  status_mode?: 'auto' | 'manual';
}

/**
 * 프로그램/강좌의 실제 상태를 KST 기준으로 계산
 * @param course 강좌 정보 (날짜/시간 필드 포함)
 * @returns 'upcoming' | 'recruiting' | 'ongoing' | 'completed'
 */
export const getActualCourseStatus = (course: CourseForStatus): 'upcoming' | 'recruiting' | 'ongoing' | 'completed' => {
  // 수동 모드인 경우 저장된 status 그대로 반환
  if (course.status_mode === 'manual' && course.status) {
    return course.status as 'upcoming' | 'recruiting' | 'ongoing' | 'completed';
  }

  // 날짜 필드가 없으면 기본값 'upcoming' 반환
  if (!course.registrationStartDate || !course.registrationEndDate ||
      !course.courseStartDate || !course.courseEndDate) {
    return 'upcoming';
  }

  const now = new Date(); // 현재 시간 (로컬)

  // KST 기준으로 각 시점 계산
  const regStart = parseKSTDateTime(
    course.registrationStartDate,
    course.registrationStartTime,
    '00:00:00'
  );
  const regEnd = parseKSTDateTime(
    course.registrationEndDate,
    course.registrationEndTime,
    '23:59:59'
  );
  const courseStart = parseKSTDateTime(course.courseStartDate, undefined, '00:00:00');
  const courseEnd = parseKSTDateTime(course.courseEndDate, undefined, '23:59:59');

  // 상태 판단 (MECE 우선순위)
  // 1. 교육 종료일 지남 → 종료 (최우선)
  if (now > courseEnd) {
    return 'completed';
  }

  // 2. 신청 기간 중 → 모집중 (모집이 열려있으면 교육 시작 여부와 무관하게 모집중)
  if (now >= regStart && now <= regEnd) {
    return 'recruiting';
  }

  // 3. 교육 기간 중 → 진행중 (모집 마감 상태)
  if (now >= courseStart && now <= courseEnd) {
    return 'ongoing';
  }

  // 4. 신청 시작 전 → 모집예정
  if (now < regStart) {
    return 'upcoming';
  }

  // 5. 신청 마감 후 ~ 교육 시작 전 (대기 구간)
  return 'upcoming';
};

/**
 * ProgramClass 타입용 상태 계산
 */
interface ProgramClassForStatus {
  registration_start_date: string;
  registration_end_date: string;
  registration_start_time?: string;
  registration_end_time?: string;
  course_start_date: string;
  course_end_date: string;
  status?: string;
  status_mode?: 'auto' | 'manual';
}

export const getActualClassStatus = (programClass: ProgramClassForStatus): 'upcoming' | 'recruiting' | 'ongoing' | 'completed' => {
  return getActualCourseStatus({
    registrationStartDate: programClass.registration_start_date,
    registrationEndDate: programClass.registration_end_date,
    registrationStartTime: programClass.registration_start_time,
    registrationEndTime: programClass.registration_end_time,
    courseStartDate: programClass.course_start_date,
    courseEndDate: programClass.course_end_date,
    status: programClass.status,
    status_mode: programClass.status_mode,
  });
};

/**
 * 강좌의 모든 반(class)을 고려한 실제 상태 계산
 * - 하나의 반이라도 모집 중이면 '모집중' 반환
 * - 모든 반이 진행 중이면 '진행중'
 * - 모든 반이 종료되면 '종료'
 * - 그 외에는 '모집예정'
 */
export const getCourseStatusWithClasses = (
  course: CourseForStatus,
  classes: ProgramClassForStatus[]
): 'upcoming' | 'recruiting' | 'ongoing' | 'completed' => {
  // 프로그램 자체가 수동 모드이면 저장된 status 반환
  if (course.status_mode === 'manual' && course.status) {
    return course.status as 'upcoming' | 'recruiting' | 'ongoing' | 'completed';
  }

  // 반이 없으면 기존 로직 사용
  if (!classes || classes.length === 0) {
    return getActualCourseStatus(course);
  }

  const now = new Date();

  // 각 반의 상태 확인
  let hasRecruiting = false;
  let hasOngoing = false;
  let hasUpcoming = false;
  let allCompleted = true;

  for (const programClass of classes) {
    // 반이 수동 모드이면 저장된 status 사용
    if (programClass.status_mode === 'manual' && programClass.status) {
      const classStatus = programClass.status as 'upcoming' | 'recruiting' | 'ongoing' | 'completed';
      if (classStatus === 'completed') continue;
      allCompleted = false;
      if (classStatus === 'recruiting') hasRecruiting = true;
      else if (classStatus === 'ongoing') hasOngoing = true;
      else if (classStatus === 'upcoming') hasUpcoming = true;
      continue;
    }

    const regStart = parseKSTDateTime(
      programClass.registration_start_date,
      programClass.registration_start_time,
      '00:00:00'
    );
    const regEnd = parseKSTDateTime(
      programClass.registration_end_date,
      programClass.registration_end_time,
      '23:59:59'
    );
    const courseStart = parseKSTDateTime(programClass.course_start_date, undefined, '00:00:00');
    const courseEnd = parseKSTDateTime(programClass.course_end_date, undefined, '23:59:59');

    // 반 상태 판단 (MECE 우선순위: completed > recruiting > ongoing > upcoming)
    if (now > courseEnd) {
      // 이 반은 종료됨
      continue;
    }

    allCompleted = false;

    if (now >= regStart && now <= regEnd) {
      hasRecruiting = true;
    } else if (now >= courseStart && now <= courseEnd) {
      hasOngoing = true;
    } else if (now < regStart) {
      hasUpcoming = true;
    }
  }

  // 우선순위: recruiting > ongoing > upcoming > completed
  // 하나라도 모집 중이면 모집중
  if (hasRecruiting) {
    return 'recruiting';
  }
  // 모든 반이 종료됨
  if (allCompleted) {
    return 'completed';
  }
  // 진행 중인 반이 있음
  if (hasOngoing) {
    return 'ongoing';
  }
  // 모집 예정인 반이 있음
  if (hasUpcoming) {
    return 'upcoming';
  }
  // 모집 마감 후 교육 시작 전 (어떤 반도 recruiting/ongoing/upcoming/completed가 아닌 경우)
  // 이 경우는 없어야 하지만, 안전을 위해 ongoing 반환 (모집 마감 상태로 간주)
  return 'ongoing';
};

// ====== 단체체험 날짜 포맷 유틸 ======

/** "2026-03-05" → "2026.03.05 (수)" */
export const formatGroupDate = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} (${dayNames[d.getDay()]})`;
};

/** "2026-03-05" → "3/5" (짧은 형식) */
export const formatGroupDateShort = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/** ISO datetime → "2026.03.05 14:30" */
export const formatGroupDateTime = (isoStr: string): string => {
  const d = new Date(isoStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// ====== 결제 입금기한 포맷 (모든 화면 단일 출처) ======

/**
 * 결제 입금기한(payment_deadline)을 모든 화면에서 동일하게 표시하는 공용 포맷터.
 * 반드시 서버가 저장한 applications.payment_deadline 값을 넣을 것. 클라이언트 재계산 금지.
 *
 * - 신청·승격·복구는 서버가 "다음 업무일 14:00"으로 저장한다(DB 함수 next_payment_deadline).
 *   정시면 분을 생략한다 (예: 10/02 14:00 → "2026년 10월 2일(금) 14시까지")
 * - 정각 자정(00:00)이면 "전날 24시까지"로 표기 (예: 06/27 00:00 → "2026년 6월 26일(금) 24시까지")
 *   → 2026-10-01 이전 신청분과 관리자 기한연장 값이 자정일 수 있다.
 * - 그 외 시각(관리자 기한연장 등)은 실제 시각 그대로 표기
 *   (예: 06/26 14:30 → "2026년 6월 26일(금) 14시 30분까지")
 *
 * 브라우저 로컬 타임존 기준(한국 사용자 = KST). 기존 포맷터들과 동일한 전제.
 */
export const formatPaymentDeadline = (deadline: string | Date | null | undefined): string => {
  if (!deadline) return '-';
  const d = new Date(deadline);
  if (isNaN(d.getTime())) return '-';
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
  const pad = (n: number) => String(n).padStart(2, '0');

  const isMidnight = d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
  if (isMidnight) {
    // 자정(00:00) = 전날 24시. 1초 전으로 옮겨 전날 날짜를 구한다.
    const prev = new Date(d.getTime() - 1000);
    return `${prev.getFullYear()}년 ${prev.getMonth() + 1}월 ${prev.getDate()}일(${dayNames[prev.getDay()]}) 24시까지`;
  }
  const time = d.getMinutes() === 0 ? `${pad(d.getHours())}시` : `${pad(d.getHours())}시 ${pad(d.getMinutes())}분`;
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일(${dayNames[d.getDay()]}) ${time}까지`;
};
